<template>
  <div v-if="!hideSortby || hasTags" class="w-full sm:w-156 2xl:w-239 flex flex-col-reverse sm:flex-row items-start justify-between mb-6">
    <div class="w-full sm:w-156 2xl:w-239 flex flex-wrap items-center justify-start">
      <FeedFilterTagFulltext
        v-if="filters.fulltext"
        :data="filters.fulltext"
        type="fulltext"
      />
      <FeedFilterTagSupplier
        v-if="filters.idSupplier"
        :data="filters.idSupplier"
        type="idSupplier"
      />
      <FeedFilterTagCategory
        v-if="filters.idCategory"
        :data="filters.idCategory"
        :type="type"
      />
      <FeedFilterTagCategory
        v-else-if="filters.idPostCategory && filters.idPostCategory !== implicitPostCategoryId"
        :data="filters.idPostCategory"
        :type="type"
      />
      <FeedFilterTagLocation
        v-if="filters.location && (type === CATEGORY_TYPE.ESHOP)"
        :data="filters.location"
        type="location"
      />
      <FeedFilterTagLocation
        v-if="filters.maxKmFromHome && (type === CATEGORY_TYPE.ESHOP)"
        :data="filters.maxKmFromHome"
        type="maxKmFromHome"
      />
      <FeedFilterTagLocation
        v-if="filters.maxKmFromLocation && (type === CATEGORY_TYPE.ESHOP)"
        :data="filters.maxKmFromLocation"
        type="maxKmFromLocation"
      />
      <FeedFilterTagParameter
        v-for="item in filters.eshopParameters"
        :key="`${type}_${item.idParameter}_${item.idValue}`"
        :data="item"
        :type="type"
      />
      <FeedContentTagParameter
        v-for="item in filters.contentParameters"
        :key="`content_${item.idParameter}_${item.idValue}`"
        :data="item"
        source="content"
      />
      <FeedContentTagParameter
        v-for="item in filters.catalogueParameters"
        :key="`catalogue_${item.idParameter}_${item.idValue}`"
        :data="item"
        source="catalogue"
      />
      <FeedClearFilters
        v-if="hasClearableFilters"
        :preserve-post-category="filters.idPostCategory === implicitPostCategoryId"
      />
    </div>
    <div v-if="!hideSortby" class="mb-2 w-full sm:w-fit flex justify-end">
      <FeedSortbyPicker :type="type" :is-detail="isDetail" />
    </div>
  </div>
</template>

<script setup>
import { useEnumStore } from '@stores/enum';
import { parameterQueryTags } from '@utils/parameters';
import { CATEGORY_TYPE } from "@utils/constants";
import FeedFilterTagFulltext from "@components/filter/feed/feed-filter-tag-fulltext";
import FeedFilterTagSupplier from "@components/filter/feed/feed-filter-tag-supplier";
import FeedFilterTagCategory from "@components/filter/feed/feed-filter-tag-category";
import FeedFilterTagLocation from "@components/filter/feed/feed-filter-tag-location";
import FeedFilterTagParameter from "@components/filter/feed/feed-filter-tag-parameter";
import FeedContentTagParameter from "@components/filter/feed/feed-content-tag-parameter";
import FeedClearFilters from "@components/filter/feed/feed-clear-filters";
import FeedSortbyPicker from "@components/filter/feed/feed-sortby-picker";

const props = defineProps({
  type: {
    type: String,
    default: 'eshop'
  },
  isDetail: {
    type: Boolean,
    default: false
  },
  hideSortby: {
    type: Boolean,
    default: false
  },
  implicitPostCategoryId: {
    type: Number,
    default: null
  }
});

const route = useRoute();

const routeName = computed(() => route?.name || '');

const enums = useEnumStore();

const filters = computed(() => ({
  idSupplier: +(route.query.idSupplier || null),
  idCategory: +(route.query.idCategory || (routeName.value.startsWith('category-slug___') ? parseInt(route.params?.slug) : null)),
  idPostCategory: +(route.query.idPostCategory || (routeName.value.startsWith('inspiration-type-slug___') || routeName.value.startsWith('news-about-slug___') ? parseInt(route.params?.slug) : null)),
  fulltext: route.query?.fulltext || null,
  location: route.query?.location || null,
  maxKmFromHome: route.query?.maxKmFromHome ? +route.query?.maxKmFromHome : null,
  maxKmFromLocation: route.query?.maxKmFromLocation ? +route.query?.maxKmFromLocation : null,
  eshopParameters: parameterQueryTags(route.query, 'p', enums.eshopParameters),
  contentParameters: parameterQueryTags(route.query, 't', enums.contentParameters),
  catalogueParameters: parameterQueryTags(route.query, 'c', enums.catalogueParameters),
}));

const hasClearableFilters = computed(() => Boolean(
  filters.value.idCategory
  || (filters.value.idPostCategory && filters.value.idPostCategory !== props.implicitPostCategoryId)
  || filters.value.location
  || filters.value.maxKmFromHome
  || filters.value.maxKmFromLocation
  || filters.value.eshopParameters?.length
  || filters.value.contentParameters?.length
  || filters.value.catalogueParameters?.length
));

const hasTags = computed(() => Boolean(
  filters.value.fulltext
  || filters.value.idSupplier
  || filters.value.idCategory
  || (filters.value.idPostCategory && filters.value.idPostCategory !== props.implicitPostCategoryId)
  || filters.value.location
  || filters.value.maxKmFromHome
  || filters.value.maxKmFromLocation
  || filters.value.eshopParameters?.length
  || filters.value.contentParameters?.length
  || filters.value.catalogueParameters?.length
));

</script>
